import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PenTool, Wrench, Banknote, ArrowRight, Check, Shield, Clock, Users, Phone } from 'lucide-react';
import axios from 'axios';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Services = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [dbItems, setDbItems] = useState([]);

  useEffect(() => {
    const load = async () => {
      try { const r = await axios.get(`${API}/admin/services-page`); setDbItems(r.data.items || []); } catch {}
    };
    load();
  }, []);

  // Override service data with DB items if available
  const getDbOverride = (section) => dbItems.find(d => d.section === section);

  const services = [
    {
      id: 'design',
      icon: PenTool,
      title: isZh ? '建筑设计' : 'Architectural Design',
      subtitle: isZh ? '专业设计团队，量身定制' : 'Expert design team, tailored to you',
      desc: isZh
        ? 'ALIGCUS 拥有经验丰富的建筑设计团队，提供从概念到施工图的全流程设计服务。我们的设计已获得多个加州城市政府的预审批，大幅缩短许可审批时间。作为设计施工一体化公司，我们的设计团队与施工团队紧密协作，确保设计方案的可行性和成本效益。'
        : 'ALIGCUS has an experienced architectural design team providing full-service design from concept to construction drawings. Our designs are pre-approved by multiple California city governments, significantly reducing permit approval time. As a design-build firm, our design and construction teams work closely together to ensure feasibility and cost-effectiveness.',
      benefits: isZh
        ? ['预审批户型方案，加速许可', '设计施工无缝衔接', '定制化设计满足个性需求', '3D渲染效果图预览']
        : ['Pre-approved plans accelerate permits', 'Seamless design-to-build handoff', 'Customizable designs for unique needs', '3D rendering previews']
    },
    {
      id: 'construction',
      icon: Wrench,
      title: isZh ? '建筑施工' : 'Construction',
      subtitle: isZh ? '精湛工艺，高效交付' : 'Expert craftsmanship, efficient delivery',
      desc: isZh
        ? '从地基到交付，ALIGCUS 提供全程专业施工服务。我们采用行业领先的建造技术和严格的质量管控体系，确保每个项目都达到最高标准。我们的一站式服务模式消除了传统建造中设计与施工脱节的问题，大幅提高效率、降低成本。'
        : 'From foundation to delivery, ALIGCUS provides comprehensive construction services. We employ industry-leading building techniques and strict quality control to ensure every project meets the highest standards. Our one-stop service model eliminates the disconnect between design and construction in traditional building, significantly improving efficiency and reducing costs.',
      benefits: isZh
        ? ['4-6个月高效施工周期', '行业领先质量标准', '全程项目经理对接', '透明定价，无隐藏费用']
        : ['4-6 month efficient build timeline', 'Industry-leading quality standards', 'Dedicated project manager', 'Transparent pricing, no hidden costs']
    },
    {
      id: 'financing',
      icon: Banknote,
      title: isZh ? '建筑贷款' : 'Construction Financing',
      subtitle: isZh ? '金融助力，轻松建房' : 'Financial support, build with ease',
      desc: isZh
        ? 'ALIGCUS 与多家专业建筑贷款机构建立了深度合作关系。我们帮助客户对接最适合的融资方案，降低建房资金门槛。设计施工一体化与金融服务的结合，让客户可以专注于规划理想家园，而无需为资金和流程分心。这是 ALIGCUS 带给客户的核心竞争优势。'
        : 'ALIGCUS has established deep partnerships with specialized construction lenders. We help clients connect with the best financing solutions, lowering the barrier to building. The combination of design-build integration with financial services allows clients to focus on planning their dream home without worrying about funding. This is the core competitive advantage ALIGCUS brings to clients.',
      benefits: isZh
        ? ['对接专业建筑贷款机构', '灵活融资方案', '全程协助贷款申请', '设计施工金融三位一体']
        : ['Connect with specialized construction lenders', 'Flexible financing options', 'Full loan application assistance', 'Design + Build + Finance, all in one']
    }
  ];

  return (
    <div className="min-h-screen bg-white ">
      <SEO
        title={isZh ? '一站式专业服务 | ADU 设计·施工·融资全流程 | ALIGCUS 阿里建筑加州' : 'One-Stop ADU Services | Design · Build · Financing All-in-One | ALIGCUS California'}
        description={isZh ? 'ALIGCUS 提供加州 ADU 全流程服务：建筑设计、施工建造、融资对接三位一体。专业建筑贷款合作伙伴，灵活融资方案。免费咨询 (626) 803-8000。' : 'ALIGCUS California ADU full-service: architectural design, construction, and financing — all integrated. Specialized construction lender partners. Free consultation (626) 803-8000.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/services' : 'https://www.aligcus.com/services'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/services' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/services' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '服务' : 'Services', url: isZh ? 'https://www.aligcus.com/zh/services' : 'https://www.aligcus.com/services' },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="bg-gray-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{isZh ? '一站式专业服务' : 'One-Stop Professional Services'}</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {isZh ? '设计、施工、金融三位一体，为您的建房之旅保驾护航' : 'Design, construction, and financing — all integrated to power your building journey'}
          </p>
        </div>
      </section>

      {/* Advantage Banner */}
      <section className="bg-red-600 py-8">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-6 text-center text-white">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-8 h-8" />
            <div className="text-left"><div className="font-bold">{isZh ? '设计施工一体化' : 'Design-Build'}</div><div className="text-sm text-red-100">{isZh ? '无缝衔接，高效交付' : 'Seamless & efficient'}</div></div>
          </div>
          <div className="flex items-center justify-center gap-3">
            <Clock className="w-8 h-8" />
            <div className="text-left"><div className="font-bold">{isZh ? '快速交付' : 'Fast Delivery'}</div><div className="text-sm text-red-100">{isZh ? '4-6个月完工' : '4-6 months completion'}</div></div>
          </div>
          <div className="flex items-center justify-center gap-3">
            <Users className="w-8 h-8" />
            <div className="text-left"><div className="font-bold">{isZh ? '金融助力' : 'Finance Support'}</div><div className="text-sm text-red-100">{isZh ? '降低资金门槛' : 'Lower the barrier'}</div></div>
          </div>
        </div>
      </section>

      {/* Service Sections */}
      {services.map((service, i) => {
        const dbOverride = getDbOverride(service.id);
        const sTitle = dbOverride ? (isZh ? dbOverride.title_zh || service.title : dbOverride.title_en || service.title) : service.title;
        const sSubtitle = dbOverride ? (isZh ? dbOverride.subtitle_zh || service.subtitle : dbOverride.subtitle_en || service.subtitle) : service.subtitle;
        const sDesc = dbOverride ? (isZh ? dbOverride.desc_zh || service.desc : dbOverride.desc_en || service.desc) : service.desc;
        const sImage = dbOverride?.image;
        const sBenefits = dbOverride ? (isZh ? dbOverride.benefits_zh : dbOverride.benefits_en)?.split('\n').filter(b => b.trim()) : null;
        const benefits = sBenefits && sBenefits.length > 0 ? sBenefits : service.benefits;
        return (
        <section key={service.id} id={service.id} className={`py-20 ${i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
          <div className="max-w-6xl mx-auto px-6">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{sTitle}</h2>
                <p className="text-lg text-red-600 font-medium mb-4">{sSubtitle}</p>
                <p className="text-gray-700 leading-relaxed mb-6">{sDesc}</p>
                <ul className="space-y-3">
                  {benefits.map((b, bi) => (
                    <li key={bi} className="flex items-center gap-3 text-gray-700">
                      <Check className="w-5 h-5 text-red-600 flex-shrink-0" />{b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl overflow-hidden flex items-center justify-center min-h-[300px] ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                {sImage ? <img src={sImage} alt={sTitle} className="w-full h-full object-cover" /> : <service.icon className="w-32 h-32 text-red-200" />}
              </div>
            </div>
          </div>
        </section>
        );
      })}

      {/* CTA */}
      <section className="bg-red-600 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">{isZh ? '体验一站式建房服务' : 'Experience One-Stop Building Service'}</h2>
          <p className="text-red-100 mb-8">{isZh ? '从设计到施工到融资，我们全程为您服务' : 'From design to construction to financing, we serve you every step'}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/estimate">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg">
                {isZh ? '免费估价' : 'Free Estimate'} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3 justify-center mt-6 max-w-lg mx-auto">
            {[{l:'SoCal',n:'(626) 803-8000',t:'6268038000'},{l:'NorCal',n:'(408) 900-6688',t:'4089006688'},{l:'San Diego',n:'(858) 910-0000',t:'8589100000'}].map(p=>(
              <a key={p.l} href={`tel:${p.t}`} className="flex flex-col items-center gap-1 px-4 py-3 bg-white/10 border border-white/30 rounded-xl hover:bg-white/20 transition-all">
                <span className="text-xs font-bold text-white/80">{p.l}</span>
                <span className="text-sm font-bold text-white">{p.n}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
